import React, { useContext, useCallback, useEffect, useState } from 'react';
import { IoClose, IoInformationCircle } from 'react-icons/io5';
import { useNavigate } from 'react-router';
import { ensureUserIdCookie } from '../../utils/userIdCookie';
import { generateQRCodeSVG } from '../../utils';
import {
  ModalOverlay,
  ModalContent,
  CloseButton,
  ModalTitle,
  ModalDesc,
  QRContainer,
  QRInstruction,
  ModalButtonContent,
  ModalLinkButton,
  TempSuccessButton,
  MetaText,
  ErrorText,
  StatusText,
} from './RegisterVerificationModal.styles';
import {
  useSessionPolling,
  type PortalSessionGetResponse,
  type VerifiedPersonResponse,
  type VerifiedPerson,
} from '../../hooks/useSessionPolling';
import useDeviceType from '../../hooks/useDevice';
import { UserContext } from '../../context/UserContextBase';

interface RegisterVerificationModalProps {
  open: boolean;
  onClose: () => void;
}

const RegisterVerificationModal: React.FC<RegisterVerificationModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const device = useDeviceType();
  const isMobile = device === 'mobile';
  const { syncFromSessionResponse, userId } = useContext(UserContext);
  const [qrPayload, setQrPayload] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionPollingEnabled, setSessionPollingEnabled] = useState<boolean>(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [expired, setExpired] = useState<boolean>(false);

  const tempSuccessRegistration = useCallback(() => {
    // Flat shape — mirrors what the VaaS callback delivers from the disclosed
    // view of a per-field VerifiedPerson SD-JWT. Keeping this in sync with the
    // VerifiedPerson type (see useSessionPolling.ts) so the temp button
    // produces a session that behaves identically to a real verification.
    const demo: VerifiedPerson = {
      givenNames: 'JOHN',
      surname: 'DOE',
      dateOfBirth: '1990-01-01',
      citizenshipCountry: 'USA',
      sex: 'M',
      sourceDocumentType: 'urn:iden2:ns:type:201910:ICAO9303Document',
      assuranceLevel: 'urn:iden2:ns:trust-framework:202503:iden2:i2a-3',
      portrait: {
        encodedImage: 'https://via.placeholder.com/150',
        mediaType: 'image/jpeg',
        documentType: 'urn:iden2:ns:type:201910:ICAO9303Document',
      },
      selfie: {
        encodedImage: 'https://via.placeholder.com/150',
        mediaType: 'image/jpeg',
        documentType: 'urn:iden2:ns:type:202503:selfie',
        createdAt: '2020-01-01',
        imageSource: 'liveness',
      },
    };
    syncFromSessionResponse({
      ok: true,
      userId: userId ?? '',
      data: demo,
      studentIdCrsCredentialId: null,
    });
    onClose();
    navigate('/home');
  }, [onClose, navigate, syncFromSessionResponse, userId]);

  const onSessionSuccess = useCallback(
    (resp: VerifiedPersonResponse) => {
      syncFromSessionResponse(resp as PortalSessionGetResponse);
      setSessionPollingEnabled(false);
      onClose();
      navigate('/home');
    },
    [onClose, navigate, syncFromSessionResponse],
  );

  const formatFailureReason = useCallback((reason: string): string => {
    switch (reason) {
      case 'invalid_vp':
        return 'The credential you presented was invalid. Please try again in the iDen2 app.';
      case 'issuer_not_trusted':
        return 'The issuer of your credential is not trusted for this verification.';
      case 'revoked_credential':
        return 'Your credential appears to have been revoked.';
      case 'policy_violation':
        return 'The credential did not meet the verification policy for this verification.';
      default:
        return 'Verification failed. Please try again.';
    }
  }, []);

  const handleSessionFailure = useCallback((reason: string) => {
    setSessionPollingEnabled(false);
    const friendly = formatFailureReason(reason);
    setError(friendly);
    setQrPayload('');
    setExpiresAt(null);
    setRemainingSeconds(null);
    setExpired(false);
  }, [formatFailureReason]);

  const { status } = useSessionPolling(sessionPollingEnabled, 2000, {
    onSuccess: onSessionSuccess,
    onFailure: handleSessionFailure,
  });

  useEffect(() => {
    if (!open) {
      setSessionPollingEnabled(false);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        setQrPayload('');
        setSessionPollingEnabled(true);
        // Ensure user id cookie is set 
        ensureUserIdCookie();
        const res = await fetch('/api/openid4vp/authorize', { credentials: 'include' });
        const json = await res.json();
        if (!res.ok || !json?.success) {
          throw new Error(json?.message || 'Failed to create OpenID4VP request');
        }
        type AuthorizeData = { openIdVpRequestUrl?: string; expiresAt?: number };
        const data = (json?.data ?? {}) as Partial<AuthorizeData>;
        if (data.openIdVpRequestUrl) {
          // ok
        } else {
          throw new Error('openIdVpRequestUrl does not exist');
        }
        const payload = data.openIdVpRequestUrl as string;
        const maybeExpires = typeof data.expiresAt === 'number' ? data.expiresAt : null;
        if (!cancelled) {
          setQrPayload(payload);
          setExpiresAt(maybeExpires);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message || 'Unexpected error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [open]);

  // Countdown and expire handling (keeps ticking after expiry to show elapsed time)
  useEffect(() => {
    if (!expiresAt) {
      setRemainingSeconds(null);
      setExpired(false);
      return;
    }
    const update = () => {
      const now = Date.now();
      const diff = Math.floor((expiresAt - now) / 1000);
      setRemainingSeconds(diff);
      setExpired(diff <= 0);
    };
    update();
    const id = window.setInterval(update, 1000);
    return () => { window.clearInterval(id); };
  }, [expiresAt]);

  if (!open) return null;
  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose} aria-label="Close">
          <IoClose />
        </CloseButton>
        <ModalTitle>Complete Your Registration</ModalTitle>
        <ModalDesc>
          To complete your registration, you'll need to verify your identity using the iDen2 app with a government-issued ID.
        </ModalDesc>
        {!isMobile && (
          <>
            <QRContainer>
              {loading && <MetaText>Generating QR...</MetaText>}
              {!loading && !error && qrPayload && (
                <div dangerouslySetInnerHTML={{ __html: generateQRCodeSVG(qrPayload, 160) }} />
              )}
              {!loading && error && <ErrorText>{error}</ErrorText>}
            </QRContainer>
            <QRInstruction>
              Scan this QR code with the iDen2 app to begin identity verification
            </QRInstruction>
          </>
        )}
        {isMobile && !loading && error && (
          <ErrorText>{error}</ErrorText>
        )}

        {!error && expiresAt && (
          <MetaText>
            Request valid until {new Date(expiresAt).toLocaleString()}
          </MetaText>
        )}
        {!error && typeof remainingSeconds === 'number' && remainingSeconds >= 0 && !expired && (
          <MetaText>
            Expires in {Math.floor(remainingSeconds / 60)}:{`${remainingSeconds % 60}`.padStart(2, '0')}
          </MetaText>
        )}
        {!error && expired && (
          <ErrorText>Request expired. Please start again.</ErrorText>
        )}
        {isMobile && (
          <ModalLinkButton
            href={expired || !qrPayload ? undefined : qrPayload}
            style={{ marginTop: 0, opacity: expired ? 0.6 : 1, pointerEvents: expired ? 'none' : 'auto' }}
            aria-disabled={expired || !qrPayload}
            tabIndex={expired || !qrPayload ? -1 : 0}
          >
            <ModalButtonContent>
              <IoInformationCircle size={20} />
              Start verification process
            </ModalButtonContent>
          </ModalLinkButton>
        )}
        {sessionPollingEnabled && !expired && (
          <StatusText>
            {status === 'success' ? 'Verified.' : 'Waiting for verification…'}
          </StatusText>
        )}
        <TempSuccessButton
          disabled={expired}
          onClick={tempSuccessRegistration}
        >
          Temp
        </TempSuccessButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default RegisterVerificationModal;
